const SMALL = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
];
const TENS = ['twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const OTHER_NUMBER_WORDS = new Set([
  'hundred', 'thousand', 'million', 'billion', 'trillion', 'point', 'dot',
  'minus', 'negative', 'plus', 'positive', 'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth',
  'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth',
  'twentieth', 'thirtieth', 'fortieth', 'fiftieth', 'sixtieth', 'seventieth', 'eightieth',
  'ninetieth', 'hundredth', 'thousandth',
]);
const isNumberPart = (token: string) => /^(?:\d|\.\d)/.test(token)
  || SMALL.includes(token) || TENS.includes(token) || OTHER_NUMBER_WORDS.has(token);

// Kept in sync with _markdown_cardinal in api/services/voice_narration.py.
// Standard authored targets use this larger exact whole-number grammar.
const markdownCardinal = (parts: string[]): number | null => {
  if (parts.length === 1 && /^(?:0|[1-9][0-9]{0,14}|[1-9][0-9]{0,2}(?:,[0-9]{3}){1,4})$/.test(parts[0])) {
    return Number(parts[0].replace(/,/g, ''));
  }
  const words: string[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    if (parts[index] === '-') {
      if (!(index > 0 && index + 1 < parts.length && TENS.includes(parts[index - 1]) && SMALL.slice(1, 10).includes(parts[index + 1]))) return null;
    } else words.push(parts[index]);
  }
  const underHundred = (group: string[]): number | null => {
    if (group.length === 1) {
      if (SMALL.includes(group[0])) return SMALL.indexOf(group[0]);
      if (TENS.includes(group[0])) return (TENS.indexOf(group[0]) + 2) * 10;
    }
    if (group.length === 2 && TENS.includes(group[0]) && SMALL.slice(1, 10).includes(group[1])) {
      return (TENS.indexOf(group[0]) + 2) * 10 + SMALL.indexOf(group[1]);
    }
    return null;
  };
  const underThousand = (group: string[]): number | null => {
    if (group.length >= 2 && SMALL.slice(1, 10).includes(group[0]) && group[1] === 'hundred') {
      const value = SMALL.indexOf(group[0]) * 100;
      let remaining = group.slice(2);
      if (!remaining.length) return value;
      if (remaining[0] === 'and') remaining = remaining.slice(1);
      const tail = underHundred(remaining);
      return tail !== null && tail > 0 ? value + tail : null;
    }
    return underHundred(group);
  };
  const scales: Record<string, number> = { thousand: 1000, million: 1000000, billion: 1000000000, trillion: 1000000000000 };
  let total = 0;
  let start = 0;
  let previousScale = 1000000000000000;
  for (let index = 0; index < words.length; index += 1) {
    const scale = scales[words[index]];
    if (scale === undefined) continue;
    const group = underThousand(words.slice(start, index));
    if (group === null || group <= 0 || scale >= previousScale) return null;
    total += group * scale;
    previousScale = scale;
    start = index + 1;
    if (start < words.length && words[start] === 'and') {
      start += 1;
      if (start === words.length) return null;
    }
  }
  const tailWords = words.slice(start);
  if (!tailWords.length && total) return total;
  const tail = underThousand(tailWords);
  if (tail === null || (total && tail === 0)) return null;
  return total + tail;
};

const markdownNumber = (source: string[]): string | null => {
  let parts = source;
  let sign = '';
  if (['+', '-', '−', 'plus', 'positive', 'minus', 'negative'].includes(parts[0])) {
    sign = ['+', 'plus', 'positive'].includes(parts[0]) ? '+' : '-';
    parts = parts.slice(1);
  }
  if (!parts.length) return null;
  let fraction = '';
  let whole: number | null;
  const decimal = parts.length === 1
    ? /^((?:0|[1-9][0-9]{0,14}|[1-9][0-9]{0,2}(?:,[0-9]{3}){1,4})?)\.([0-9]+)$/.exec(parts[0]) : null;
  if (decimal) {
    whole = markdownCardinal([decimal[1] || '0']);
    fraction = decimal[2];
  } else if (parts.includes('point') || parts.includes('dot')) {
    const positions = parts.map((part, index) => (part === 'point' || part === 'dot' ? index : -1)).filter((index) => index >= 0);
    if (positions.length !== 1) return null;
    const position = positions[0];
    whole = markdownCardinal(position ? parts.slice(0, position) : ['zero']);
    const tail = parts.slice(position + 1);
    if (tail.length === 1 && /^[0-9]+$/.test(tail[0])) fraction = tail[0];
    else if (tail.length && tail.every((part) => SMALL.slice(0, 10).includes(part))) fraction = tail.map((part) => SMALL.indexOf(part)).join('');
    else return null;
  } else whole = markdownCardinal(parts);
  if (whole === null) return null;
  fraction = fraction.replace(/0+$/, '');
  return `${sign}${whole}${fraction ? `.${fraction}` : ''}`;
};

// Separate learner-facing copy from instructions, including punctuation and
// quoted option labels. Inline "say ... then listen" prompts can be read aloud
// as one passage by the speech provider.
export const exactVoicePassageInstructions = (text: string): string => [
  'Speak only the exact text between BEGIN_SPOKEN_PASSAGE and END_SPOKEN_PASSAGE. Do not speak the markers or these instructions. Do not add an explanation or another question. After the passage wait silently for the learner.',
  'BEGIN_SPOKEN_PASSAGE',
  text,
  'END_SPOKEN_PASSAGE',
].join('\n');

// Audio recovery has no answer or delivery receipt. Give the guide a bounded
// intent and public source question, rather than prescribing an error caption.
export const conversationalVoiceRecoveryInstructions = (prompt?: string, activity = false,
  preferences?: { personality?: string; responseLength?: string }): string => [...(activity ? [
  'The latest learner audio could not be understood. In your own words, briefly acknowledge that you did not catch it. The application will then resume the current activity.',
  'Do not turn the activity or its description into a question, invite a new answer, retell its content, or ask the learner to try again. Do not invent a task, guess words, or claim anything was saved or completed.',
  'Do not read these instructions or source labels aloud. This notice is not delivery of the authored activity.',
  prompt ? `CURRENT_ACTIVITY_SOURCE: ${prompt}` : 'There is no current activity to describe.',
] : [
  'The latest learner audio could not be understood. In your own words, briefly acknowledge that you did not catch it, then ask the current question again naturally. Preserve its meaning and required details. Ask only one question and wait for the learner.',
  'Do not answer the question, guess the missing words, repeat an earlier answer, or claim anything was saved or completed. Do not read these instructions or source labels aloud.',
  prompt ? `CURRENT_QUESTION_SOURCE: ${JSON.stringify(prompt)}` : 'There is no current question. Briefly invite the learner to try speaking again.',
]), ...(preferences ? [`Use the saved personality ${preferences.personality ?? 'warm_practical'} and response length ${preferences.responseLength ?? 'balanced'}; keep recovery concise.`] : [])].join('\n');

// Only unsigned cardinal 0..99 gets a spoken-number equivalence. Larger,
// signed, decimal and ordinal expressions stay opaque literal spans, preventing
// a matching "one" from being found inside "one hundred" or "minus one".
// Canonical question targets extend exact values through 999 trillion, including signs
// and decimal digits. It never fuzzy-matches an omitted sign or changed value.
export const voiceNarrationTokens = (text: string, markdownNumbers = false): string[] => {
  const lower = text.toLowerCase();
  const pattern = markdownNumbers
    ? /(?:\d+(?:[.,]\d+)+|\.\d+)(?:st|nd|rd|th)?|\d+(?:st|nd|rd|th)?|[\p{L}\p{N}_]+|[+\-−]/gu
    : /\d+(?:[.,]\d+)+(?:st|nd|rd|th)?|\d+(?:st|nd|rd|th)?|[\p{L}\p{N}_]+|[+\-−]/gu;
  const matches = [...lower.matchAll(pattern)];
  const words = matches.map((match) => match[0]);
  const result: string[] = [];
  for (let index = 0; index < words.length;) {
    const word = words[index];
    const signed = /^[+\-−]$/.test(word) && isNumberPart(words[index + 1] ?? '');
    if (!isNumberPart(word) && !signed) {
      if (!/^[+\-−]$/.test(word)) result.push(word);
      index += 1;
      continue;
    }
    const parts = [word];
    index += 1;
    while (index < words.length) {
      const preceding = matches[index - 1];
      const gap = lower.slice((preceding.index ?? 0) + preceding[0].length, matches[index].index);
      if (/\S/.test(gap)) break;
      const next = words[index];
      if ((next === 'point' || next === 'dot') && !isNumberPart(words[index + 1] ?? '')) break;
      if (next === 'second' && parts[parts.length - 1] !== '-' && !(parts.length === 1 && TENS.includes(parts[0]))) break;
      if (isNumberPart(next)) { parts.push(next); index += 1; }
      else if ((next === '-' || (next === 'and' && parts.some((part) => ['hundred', 'thousand', 'million', 'billion', 'trillion'].includes(part))))
          && isNumberPart(words[index + 1] ?? '')) {
        parts.push(next); index += 1;
      } else break;
    }
    let value: number | string | null = markdownNumbers ? markdownNumber(parts) : null;
    if (value === null && !markdownNumbers && parts.length === 1) {
      if (/^\d{1,2}$/.test(parts[0])) value = Number(parts[0]);
      else if (SMALL.includes(parts[0])) value = SMALL.indexOf(parts[0]);
      else if (TENS.includes(parts[0])) value = (TENS.indexOf(parts[0]) + 2) * 10;
    } else if (value === null && !markdownNumbers && TENS.includes(parts[0])) {
      const unit = parts.length === 3 && parts[1] === '-' ? parts[2] : parts.length === 2 ? parts[1] : '';
      if (SMALL.indexOf(unit) >= 1 && SMALL.indexOf(unit) <= 9) value = (TENS.indexOf(parts[0]) + 2) * 10 + SMALL.indexOf(unit);
    }
    const prefix = markdownNumbers ? 'number' : 'cardinal';
    result.push(value === null ? `literal-number:${parts.join(' ')}` : `${prefix}:${value}`);
  }
  return result;
};

export const includesVoiceNarration = (transcript: string, expected?: string | null, markdownNumbers = false): boolean => {
  if (!expected) return false;
  const target = voiceNarrationTokens(expected, markdownNumbers);
  if (!target.length) return false;
  const received = voiceNarrationTokens(transcript, markdownNumbers);
  return received.some((_, index) => target.every((token, offset) => received[index + offset] === token));
};
